import React, { useEffect, useState, useCallback, useRef } from "react";
import { ClipboardList, PlusCircle, Scale, Users, HelpCircle, ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import Swal from "sweetalert2";
import "../css/App.css";
import API_BASE from "./apiBase";
import NewRequest from "./new_request";
import YourRights from "./your_rights";
import Help from "./help";
import { createSessionCacheKey, readSessionCache, writeSessionCache } from './sessionDataCache';
import { formatCompactNumber } from './numberFormatUtils';
import maskUtils from './maskUtils';

const { maskEmail, maskPhone } = maskUtils;

const T = {
  bg: "#f4f6fa",
  surface: "#ffffff",
  surfaceAlt: "#f9fafb",
  border: "#e5e7eb",
  text: "#111827",
  textSec: "#4b5563",
  textMuted: "#9ca3af",
  brand: "#2563eb",
  brandLight: "#eff6ff",
  brandBorder: "#bfdbfe",
};

const STATUS_CFG = {
  open: { bg: "#fefce8", color: "#854d0e", border: "#fde047", dot: "#ca8a04", label: "Open" },
  pending: { bg: "#fefce8", color: "#854d0e", border: "#fde047", dot: "#ca8a04", label: "Open" },
  new: { bg: "#fefce8", color: "#854d0e", border: "#fde047", dot: "#ca8a04", label: "Open" },
  "in-progress": { bg: "#eff6ff", color: "#1e40af", border: "#93c5fd", dot: "#3b82f6", label: "In Progress" },
  approved: { bg: "#f0fdf4", color: "#166534", border: "#86efac", dot: "#22c55e", label: "Approved" },
  rejected: { bg: "#fef2f2", color: "#991b1b", border: "#fca5a5", dot: "#ef4444", label: "Rejected" },
};

const getName = (s) => s.fullName || s.full_name || s.name || s.declarationName || s.declaration_name || "";
const getRawEmail = (s) => String(s.email || s.email_address || s.dsEmail || s.ds_email || s.contact_email || "").toLowerCase().trim();
const getEmail = (s) => maskEmail(getRawEmail(s));
const getPhone = (s) => maskPhone(s.contactPhone || s.contact_phone || s.phone || "");
const getStatus = (s) => {
  const raw = String(s.status || s.request_status || "").toLowerCase().trim();
  if (!raw || raw === "pending" || raw === "new") return "open";
  return raw;
};
const getRequestTypes = (s) => {
  const rt = s.requestType || s.request_type || [];
  if (Array.isArray(rt)) return rt.join(", ");
  return String(rt || "");
};
const getDate = (s) => s.createdAt || s.created_at || s.submittedAt || s.submitted_at || s.date || null;
const getStoredTenantName = () => {
  try {
    const user = window.USER || {};
    return user.tenant_name || user.tenantName || user.organization_name || user.organizationName ||
      localStorage.getItem("tenant_name") || localStorage.getItem("tenantName") || "";
  } catch (e) { return ""; }
};
const isLocalHost = () => typeof window !== "undefined" && /(localhost|127\.0\.0\.1)/i.test(window.location.hostname);

// ─── Safe single URL from API_BASE (handles comma-separated) ─────────────────
function getSafeBase() {
  const raw = String(API_BASE || "").trim();
  if (!raw) return "";
  const first = raw.split(",")[0].trim().replace(/\/$/, "");
  if (/^https?:\/\//.test(first) || first.startsWith("/")) return first;
  if (/^[\w.-]+(:\d+)?$/.test(first)) return "http://" + first;
  return first;
}

function getDsarApiUrls() {
  const base = getSafeBase();
  const urls = [];
  if (base) {
    if (base.endsWith("/api")) {
      urls.push(`${base}/manager/dsar`);
      urls.push(`${base.slice(0, -4)}/manager/dsar`);
    } else {
      urls.push(`${base}/manager/dsar`);
      urls.push(`${base}/api/manager/dsar`);
    }
  }
  urls.push("/api/manager/dsar");
  urls.push("/manager/dsar");
  if (isLocalHost() && base && !base.includes("localhost") && !base.includes("127.0.0.1")) {
    urls.push("http://localhost:8000/manager/dsar");
  }
  return [...new Set(urls.filter(Boolean))];
}

// ─── UI Atoms ─────────────────────────────────────────────────────────────────
function Pill({ children, bg, color, border, dot }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: bg, color, border: `1px solid ${border}` }}>
      {dot && <span style={{ width: 5, height: 5, borderRadius: "50%", background: dot, flexShrink: 0 }} />}
      {children}
    </span>
  );
}

function StatusBadge({ status }) {
  const k = status ? String(status).toLowerCase() : "open";
  const c = STATUS_CFG[k] || STATUS_CFG.open;
  return <Pill bg={c.bg} color={c.color} border={c.border} dot={c.dot}>{c.label}</Pill>;
}

function SlaBadge({ isBreached, noDate }) {
  if (noDate) return <span style={{ color: T.textMuted, fontSize: 12 }}>—</span>;
  return isBreached
    ? <Pill bg="#fef2f2" color="#991b1b" border="#fca5a5" dot="#ef4444">Breached</Pill>
    : <Pill bg="#f0fdf4" color="#166534" border="#86efac" dot="#22c55e">Within SLA</Pill>;
}

function JiraBadge({ status }) {
  const MAP = {
    "Created": { bg: "#f0fdf4", color: "#166534", border: "#86efac", label: "✓ Created" },
    "In Process": { bg: "#fefce8", color: "#854d0e", border: "#fde047", label: "⟳ Creating" },
    "Not Created": { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb", label: "— Not Created" },
  };
  const c = MAP[status] || MAP["Not Created"];
  return <Pill bg={c.bg} color={c.color} border={c.border}>{c.label}</Pill>;
}

function IconBtn({ title, onClick, disabled, tone = "default", children }) {
  const TONES = {
    default: { bg: "#f9fafb", border: "#e5e7eb", hover: "#f3f4f6" },
    green: { bg: "#f0fdf4", border: "#86efac", hover: "#dcfce7" },
    blue: { bg: "#eff6ff", border: "#93c5fd", hover: "#dbeafe" },
    amber: { bg: "#fefce8", border: "#fde047", hover: "#fef9c3" },
  };
  const s = TONES[tone];
  return (
    <button title={title} disabled={disabled} onClick={onClick} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 7, border: `1px solid ${s.border}`, background: s.bg, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, fontSize: 13, flexShrink: 0 }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = s.hover; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = s.bg; }}
    >{children}</button>
  );
}

function NavItem({ icon, label, active, collapsed, onClick }) {
  return (
    <div onClick={onClick} title={collapsed ? label : ""} style={{ display: "flex", alignItems: "center", gap: 10, padding: collapsed ? "10px 0" : "9px 12px", justifyContent: collapsed ? "center" : "flex-start", borderRadius: 8, cursor: "pointer", marginBottom: 4, background: active ? "#eff6ff" : "transparent", color: active ? "#2563eb" : "#374151", fontWeight: active ? 700 : 500, fontSize: 13.5, borderLeft: active ? "3px solid #2563eb" : "3px solid transparent", transition: "all 0.15s ease" }}
      onMouseEnter={e => { if (!active) { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.color = "#111827"; } }}
      onMouseLeave={e => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#374151"; } }}
    >
      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</span>
      {!collapsed && <span style={{ whiteSpace: "nowrap" }}>{label}</span>}
    </div>
  );
}

// ─── CLICKABLE STAT CARD ──────────────────────────────────────────────────────
function StatCard({ label, value, accent, active, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: T.surface,
      border: active ? `2px solid ${accent}` : `1px solid ${T.border}`,
      borderTop: `3px solid ${accent}`,
      borderRadius: 10,
      padding: "16px 18px",
      flex: 1, minWidth: 100,
      cursor: "pointer",
      boxShadow: active ? `0 0 0 3px ${accent}25` : "0 1px 3px rgba(0,0,0,0.04)",
      transition: "all 0.15s",
      userSelect: "none",
      position: "relative",
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}
    >
      {active && <div style={{ position: "absolute", top: 6, right: 8, width: 7, height: 7, borderRadius: "50%", background: accent }} />}
      <div style={{ fontSize: 28, fontWeight: 700, color: T.text, lineHeight: 1 }} title={Number(value || 0).toLocaleString()}>{formatCompactNumber(value)}</div>
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      {active && <div style={{ fontSize: 10, color: accent, marginTop: 4, fontWeight: 600 }}>● Filtered</div>}
    </div>
  );
}

// ─── SUBMISSION DETAIL MODAL ──────────────────────────────────────────────────
const SubmissionDetailModal = ({ sub, onClose, slaDays, jiraTickets, approvedIds, tenantName }) => {
  if (!sub) return null;

  const name = getName(sub) || "—";
  const email = getEmail(sub) || "—";
  const phone = getPhone(sub) || "—";
  const reqType = getRequestTypes(sub) || "—";
  const status = getStatus(sub);
  const jiraInfo = jiraTickets?.[sub.id];
  const isApproved = approvedIds?.has(String(sub.id)) || status === "approved";

  const parsed = (() => {
    const raw = getDate(sub);
    if (!raw) return null;
    try {
      const s = String(raw).trim();
      const d = /^\d{4}-\d{2}-\d{2}[T ]/.test(s)
        ? new Date(s.replace(" ", "T") + (s.includes("Z") ? "" : "Z"))
        : new Date(s);
      if (isNaN(d)) return null;
      return {
        date: d,
        display: new Intl.DateTimeFormat("en-IN", {
          timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit",
          year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
        }).format(d),
      };
    } catch (e) { return null; }
  })();

  const diffDays = parsed ? (Date.now() - parsed.date.getTime()) / 864e5 : null;
  const isBreached = diffDays != null ? diffDays > slaDays : false;
  const jiraStatus = jiraInfo?.status === "created" ? "Created" : jiraInfo?.status === "creating" ? "In Process" : "Not Created";

  const initials = name !== "—"
    ? name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase()
    : email.substring(0, 2).toUpperCase();

  const statusCfg = STATUS_CFG[status] || STATUS_CFG.open;

  // All fields to show
  const allFields = [
    ["Request ID", `DSAR-${String(sub.id).padStart(6, "0")}`],
    ["Full Name", sub.fullName || sub.full_name || sub.name || sub.declarationName || null],
    ["Email", maskEmail(sub.email || sub.email_address || sub.dsEmail || sub.ds_email || null)],
    ["Phone", maskPhone(sub.contactPhone || sub.contact_phone || sub.phone || null)],
    ["Request Type", reqType],
    ["Submitted On", parsed ? parsed.display : null],
    ["Status", null], // rendered separately
    ["SLA", null], // rendered separately
    ["Jira", null], // rendered separately
    ["Entity", sub.entity || null],
    ["Department", sub.department || null],
    ["Address", sub.current_address || sub.currentAddress || null],
    ["Former Names", sub.former_names || sub.formerNames || null],
    ["DOB", sub.dob || sub.date_of_birth || null],
    ["Nationality", sub.nationality || null],
    ["Description", sub.description || sub.notes || sub.message || null],
    ["Tenant ID", sub.tenant_id || sub.tenantId || null],
    ["Organization", sub.org_name || sub.organization_name || sub.organizationName || sub.tenant_name || sub.tenantName || tenantName || null],
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(15,23,42,0.55)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "32px 16px", overflowY: "auto",
      }}
    >
      <div style={{
        background: "#fff", borderRadius: 16, width: "100%", maxWidth: 580,
        border: "1px solid #e2e8f0", boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
      }}>
        {/* Modal Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
            DSAR-{String(sub.id).padStart(6, "0")} — {name}
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, borderRadius: 8, color: "#94a3b8", fontSize: 18, lineHeight: 1 }}>✕</button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: "18px 20px", maxHeight: "75vh", overflowY: "auto" }}>

          {/* Hero Card */}
          <div style={{
            display: "flex", alignItems: "center", gap: 14,
            padding: "14px 16px",
            background: "linear-gradient(135deg,#eff6ff,#eef2ff)",
            borderRadius: 12, marginBottom: 16, border: "1px solid #dbeafe",
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: "50%",
              background: "linear-gradient(135deg,#2563eb,#4f46e5)",
              color: "#fff", display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: 18, fontWeight: 700, flexShrink: 0,
            }}>{initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 3 }}>{name}</div>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 7 }}>
                {[email !== "—" ? email : null, phone !== "—" ? phone : null].filter(Boolean).join("  ·  ") || "—"}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <Pill bg={statusCfg.bg} color={statusCfg.color} border={statusCfg.border} dot={statusCfg.dot}>
                  {statusCfg.label}
                </Pill>
                {isApproved && (
                  <Pill bg="#f0fdf4" color="#166534" border="#86efac">✓ Approved</Pill>
                )}
                {isBreached
                  ? <Pill bg="#fef2f2" color="#991b1b" border="#fca5a5" dot="#ef4444">SLA Breached</Pill>
                  : parsed
                    ? <Pill bg="#f0fdf4" color="#166534" border="#86efac" dot="#22c55e">Within SLA</Pill>
                    : null
                }
                {jiraStatus === "Created" && (
                  <Pill bg="#f0fdf4" color="#166534" border="#86efac">✓ Jira Created</Pill>
                )}
              </div>
            </div>
          </div>

          {/* Status + SLA + Jira boxes */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
            <div style={{ background: statusCfg.bg, borderRadius: 10, padding: "10px 12px", textAlign: "center", border: `1px solid ${statusCfg.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: statusCfg.color }}>{statusCfg.label}</div>
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3, textTransform: "uppercase", letterSpacing: ".05em" }}>Status</div>
            </div>
            <div style={{ background: isBreached ? "#fef2f2" : "#f0fdf4", borderRadius: 10, padding: "10px 12px", textAlign: "center", border: `1px solid ${isBreached ? "#fca5a5" : "#86efac"}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: isBreached ? "#991b1b" : "#166534" }}>
                {parsed ? (isBreached ? "Breached" : "Within SLA") : "—"}
              </div>
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3, textTransform: "uppercase", letterSpacing: ".05em" }}>SLA ({slaDays}d)</div>
            </div>
            <div style={{ background: jiraStatus === "Created" ? "#f0fdf4" : "#f8fafc", borderRadius: 10, padding: "10px 12px", textAlign: "center", border: `1px solid ${jiraStatus === "Created" ? "#86efac" : "#e2e8f0"}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: jiraStatus === "Created" ? "#166534" : "#64748b" }}>
                {jiraStatus}
              </div>
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3, textTransform: "uppercase", letterSpacing: ".05em" }}>Jira Ticket</div>
            </div>
          </div>

          {/* Jira link if available */}
          {jiraInfo?.ticketUrl && (
            <div style={{ marginBottom: 14, padding: "9px 13px", background: "#eff6ff", borderRadius: 9, border: "1px solid #bfdbfe", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, color: "#1e40af", fontWeight: 600 }}>🎟 {jiraInfo.ticketKey}</span>
              <a href={jiraInfo.ticketUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#2563eb", fontWeight: 700, textDecoration: "none" }}>Open in Jira ↗</a>
            </div>
          )}

          {/* All Fields */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 8 }}>Request Details</div>
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
            {allFields.map(([label, val], i) => (
              <div key={label} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "9px 14px",
                background: i % 2 === 0 ? "#fff" : "#fafafa",
                borderTop: i > 0 ? "1px solid #f1f5f9" : "none",
                gap: 16,
              }}>
                <span style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".04em", flexShrink: 0 }}>{label}</span>
                <span style={{
                  fontSize: 13, fontWeight: 600, color: label === "Email" ? "#2563eb" : "#1e293b",
                  textAlign: "right", wordBreak: "break-all",
                }}>{String(val)}</span>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
};
// ─── REQUESTER DETAIL MODAL ───────────────────────────────────────────────────
const RequesterDetailModal = ({ email, requests, onClose, slaDays, jiraTickets, approvedIds, tenantName }) => {
  const [activeFilter, setActiveFilter] = useState(null);

  if (!email || !requests) return null;

  const name = (() => {
    const n = getName(requests[0]);
    return n || email;
  })();

  const initials = name
    .split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();

  const total = requests.length;
  const open = requests.filter(s => ["open", "pending", "new"].includes(getStatus(s))).length;
  const inProgress = requests.filter(s => getStatus(s) === "in-progress").length;
  const approved = requests.filter(s => getStatus(s) === "approved").length;
  const rejected = requests.filter(s => getStatus(s) === "rejected").length;

  // ── Filter logic ──
  const filteredRequests = (() => {
    if (!activeFilter || activeFilter === "total") return requests;
    if (activeFilter === "open") return requests.filter(s => ["open", "pending", "new"].includes(getStatus(s)));
    if (activeFilter === "in-progress") return requests.filter(s => getStatus(s) === "in-progress");
    if (activeFilter === "approved") return requests.filter(s => getStatus(s) === "approved");
    if (activeFilter === "rejected") return requests.filter(s => getStatus(s) === "rejected");
    return requests;
  })();

  const statButtons = [
    { key: "total", label: "Total", value: total, bg: "#f8fafc", activeBg: "#e2e8f0", color: "#475569", border: "#e2e8f0", activeBorder: "#94a3b8" },
    { key: "open", label: "Open", value: open, bg: "#fefce8", activeBg: "#fef08a", color: "#854d0e", border: "#fde047", activeBorder: "#ca8a04" },
    { key: "in-progress", label: "In Progress", value: inProgress, bg: "#eff6ff", activeBg: "#bfdbfe", color: "#1e40af", border: "#93c5fd", activeBorder: "#3b82f6" },
    { key: "approved", label: "Approved", value: approved, bg: "#f0fdf4", activeBg: "#bbf7d0", color: "#166534", border: "#86efac", activeBorder: "#22c55e" },
    { key: "rejected", label: "Rejected", value: rejected, bg: "#fef2f2", activeBg: "#fecaca", color: "#991b1b", border: "#fca5a5", activeBorder: "#ef4444" },
  ];

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(15,23,42,0.55)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "32px 16px", overflowY: "auto",
      }}
    >
      <div style={{
        background: "#fff", borderRadius: 16, width: "100%", maxWidth: 680,
        border: "1px solid #e2e8f0", boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
      }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
            👤 {email}
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, borderRadius: 8, color: "#94a3b8", fontSize: 18, lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: "18px 20px", maxHeight: "75vh", overflowY: "auto" }}>

          {/* Hero Card */}
          <div style={{
            display: "flex", alignItems: "center", gap: 14,
            padding: "14px 16px",
            background: "linear-gradient(135deg,#eff6ff,#eef2ff)",
            borderRadius: 12, marginBottom: 16, border: "1px solid #dbeafe",
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: "50%",
              background: "linear-gradient(135deg,#2563eb,#4f46e5)",
              color: "#fff", display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: 18, fontWeight: 700, flexShrink: 0,
            }}>{initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 3 }}>{name}</div>
              <div style={{ fontSize: 12, color: "#2563eb", marginBottom: 7 }}>{email}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <span style={{ padding: "2px 9px", borderRadius: 99, fontSize: 10, fontWeight: 700, background: "#eff6ff", color: "#1e40af", border: "1px solid #bfdbfe" }}>
                  📋 {total} Total Request{total !== 1 ? "s" : ""}
                </span>
                {open > 0 && (
                  <span style={{ padding: "2px 9px", borderRadius: 99, fontSize: 10, fontWeight: 700, background: "#fefce8", color: "#854d0e", border: "1px solid #fde047" }}>
                    ⏳ {open} Open
                  </span>
                )}
                {approved > 0 && (
                  <span style={{ padding: "2px 9px", borderRadius: 99, fontSize: 10, fontWeight: 700, background: "#f0fdf4", color: "#166534", border: "1px solid #86efac" }}>
                    ✓ {approved} Approved
                  </span>
                )}
                {rejected > 0 && (
                  <span style={{ padding: "2px 9px", borderRadius: 99, fontSize: 10, fontWeight: 700, background: "#fef2f2", color: "#991b1b", border: "1px solid #fca5a5" }}>
                    ✕ {rejected} Rejected
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── CLICKABLE STAT BUTTONS ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8, marginBottom: 12 }}>
            {statButtons.map(s => {
              const isActive = activeFilter === s.key;
              return (
                <div
                  key={s.key}
                  onClick={() => setActiveFilter(isActive ? null : s.key)}
                  style={{
                    background: isActive ? s.activeBg : s.bg,
                    border: `2px solid ${isActive ? s.activeBorder : s.border}`,
                    borderRadius: 10, padding: "10px 8px", textAlign: "center",
                    cursor: "pointer", userSelect: "none",
                    boxShadow: isActive ? `0 0 0 3px ${s.activeBorder}40` : "none",
                    transform: isActive ? "translateY(-2px)" : "none",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = s.activeBg;
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = s.bg;
                      e.currentTarget.style.transform = "none";
                    }
                  }}
                >
                  <div style={{ fontSize: 20, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 9, color: s.color, marginTop: 4, textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700 }}>{s.label}</div>
                  {isActive && <div style={{ fontSize: 9, color: s.color, marginTop: 3, opacity: 0.7 }}>● Active</div>}
                </div>
              );
            })}
          </div>

          {/* ── Active filter banner ── */}
          {activeFilter && activeFilter !== "total" && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "8px 13px", marginBottom: 12,
              background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, fontSize: 12,
            }}>
              <span style={{ color: "#1e40af", fontWeight: 600 }}>
                🔍 Showing: <strong style={{ textTransform: "capitalize" }}>
                  {activeFilter === "in-progress" ? "In Progress" : activeFilter}
                </strong> — {filteredRequests.length} request{filteredRequests.length !== 1 ? "s" : ""}
              </span>
              <button
                onClick={() => setActiveFilter(null)}
                style={{ background: "none", border: "none", color: "#2563eb", cursor: "pointer", fontWeight: 700, fontSize: 12, padding: "2px 6px", borderRadius: 4 }}
              >Clear ✕</button>
            </div>
          )}

          {/* ── No results ── */}
          {filteredRequests.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#94a3b8", fontSize: 13 }}>
              No requests found for this filter.
            </div>
          )}

          {/* ── Request History Title ── */}
          {filteredRequests.length > 0 && (
            <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 10 }}>
              {activeFilter && activeFilter !== "total"
                ? `${activeFilter === "in-progress" ? "In Progress" : activeFilter} Requests (${filteredRequests.length})`
                : `All Requests (${total})`
              }
            </div>
          )}

          {/* ── Each Request Card ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filteredRequests.map((sub, i) => {
              const status = getStatus(sub);
              const statusCfg = STATUS_CFG[status] || STATUS_CFG.open;
              const reqType = getRequestTypes(sub) || "—";
              const jiraInfo = jiraTickets?.[sub.id];

              const parsed = (() => {
                const raw = getDate(sub);
                if (!raw) return null;
                try {
                  const s = String(raw).trim();
                  const d = /^\d{4}-\d{2}-\d{2}[T ]/.test(s)
                    ? new Date(s.replace(" ", "T") + (s.includes("Z") ? "" : "Z"))
                    : new Date(s);
                  if (isNaN(d)) return null;
                  return new Intl.DateTimeFormat("en-IN", {
                    timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
                    year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true,
                  }).format(d);
                } catch (e) { return null; }
              })();

              const diffDays = (() => {
                const raw = getDate(sub);
                if (!raw) return null;
                try {
                  const s = String(raw).trim();
                  const d = /^\d{4}-\d{2}-\d{2}[T ]/.test(s)
                    ? new Date(s.replace(" ", "T") + (s.includes("Z") ? "" : "Z"))
                    : new Date(s);
                  if (isNaN(d)) return null;
                  return (Date.now() - d.getTime()) / 864e5;
                } catch (e) { return null; }
              })();
              const isBreached = diffDays != null ? diffDays > slaDays : false;

              return (
                <div key={sub.id || i} style={{
                  border: `1px solid ${statusCfg.border}`,
                  borderLeft: `4px solid ${statusCfg.dot || statusCfg.border}`,
                  borderRadius: 10, padding: "13px 14px",
                  background: i % 2 === 0 ? "#fff" : "#fafafa",
                  transition: "box-shadow 0.15s",
                }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                >
                  {/* Row 1: ID + Status badges */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
                    <span style={{
                      fontSize: 12, fontFamily: "monospace", color: "#2563eb", fontWeight: 700,
                      background: "#eff6ff", padding: "2px 8px", borderRadius: 5, border: "1px solid #bfdbfe",
                    }}>
                      DSAR-{String(sub.id).padStart(6, "0")}
                    </span>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                      <Pill bg={statusCfg.bg} color={statusCfg.color} border={statusCfg.border} dot={statusCfg.dot}>
                        {statusCfg.label}
                      </Pill>
                      {isBreached
                        ? <Pill bg="#fef2f2" color="#991b1b" border="#fca5a5" dot="#ef4444">SLA Breached</Pill>
                        : parsed
                          ? <Pill bg="#f0fdf4" color="#166534" border="#86efac" dot="#22c55e">Within SLA</Pill>
                          : null
                      }
                      {jiraInfo?.status === "created" && (
                        <Pill bg="#f0fdf4" color="#166534" border="#86efac">✓ Jira</Pill>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Request Type */}
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", marginBottom: 6 }}>
                    📋 {reqType}
                  </div>

                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>
                    🏢 {sub.org_name || sub.organization_name || sub.organizationName || sub.tenant_name || sub.tenantName || tenantName || "Organization not available"}
                  </div>

                  {/* Row 3: Date + description */}
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    {parsed && (
                      <span style={{ fontSize: 11, color: "#64748b" }}>🕐 {parsed}</span>
                    )}
                    {(sub.description || sub.notes || sub.message) && (
                      <span style={{ fontSize: 11, color: "#64748b", flex: 1 }}>
                        💬 {String(sub.description || sub.notes || sub.message).substring(0, 80)}
                        {String(sub.description || sub.notes || sub.message).length > 80 ? "…" : ""}
                      </span>
                    )}
                  </div>

                  {/* Jira link */}
                  {jiraInfo?.ticketUrl && (
                    <div style={{ marginTop: 8 }}>
                      <a href={jiraInfo.ticketUrl} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 11, color: "#2563eb", fontWeight: 600, textDecoration: "none" }}>
                        🎟 {jiraInfo.ticketKey} — Open in Jira ↗
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
};
// ─── MAIN DASHBOARD ──────────────────────────────────────────────────────────
const ITEMS_PER_PAGE = 10;

const DSARDashboardUser = () => {
  const cacheKey = createSessionCacheKey('dsar-user-dashboard', null, {
    tenantId: window.USER?.tenant_id || localStorage.getItem('tenant_id') || '0',
    outsideEmail: localStorage.getItem('dsar_user_email') || '',
    userType: localStorage.getItem('dsar_user_type') || '',
  });
  const [cachedData] = useState(() => readSessionCache(cacheKey));
  const [activeSection, setActiveSection] = useState("my-requests");
  const [collapsed, setCollapsed] = useState(false);
  const [submissions, setSubmissions] = useState(() => cachedData?.submissions || []);
  const [loading, setLoading] = useState(() => !cachedData);
  const [fetchError, setFetchError] = useState(null);
  const [toast, setToast] = useState(null);
  const [approvedIds, setApprovedIds] = useState(new Set());
  const [approving, setApproving] = useState({});
  const [jiraTickets, setJiraTickets] = useState({});
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [slaDays, setSlaDays] = useState(30);
  const [tenantName, setTenantName] = useState(getStoredTenantName);
  const [search, setSearch] = useState("");
  const [isOutsideUser, setIsOutsideUser] = useState(false);

  // ─── Card filter state ────────────────────────────────────────────────────
  const [cardFilter, setCardFilter] = useState(null);
  const [detailSub, setDetailSub] = useState(null);
  const [requesterModal, setRequesterModal] = useState(null);
  const [consentModal, setConsentModal] = useState(null);

  // Use ref to hold submissions so async actions always see latest value
  const submissionsRef = useRef([]);
  const hasCachedDataRef = useRef(Boolean(cachedData));
  useEffect(() => { submissionsRef.current = submissions; }, [submissions]);

  const _storageKey = (() => {
    try { const tid = window.USER?.tenant_id || localStorage.getItem("tenant_id") || "0"; return `dsar_approved_ids_${tid}`; }
    catch (e) { return "dsar_approved_ids_0"; }
  })();

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const showProcessingAlert = (title = "Processing request") => {
    Swal.fire({
      title,
      text: "Please wait a moment...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
      customClass: { popup: "swal-popup-custom" },
    });
  };

  const showResultAlert = async (title, text, type = "success") => {
    Swal.close();
    try {
      await Swal.fire({
        icon: type,
        title,
        text,
        timer: 2200,
        showConfirmButton: false,
        customClass: { popup: "swal-popup-custom" },
      });
    } catch (e) {
      showToast(text, type === "error" ? "error" : "success");
    }
  };

  function getHeaders() {
    const h = {};
    const k = process.env.REACT_APP_MANAGER_API_KEY || localStorage.getItem("manager_api_key") || "";
    const t = process.env.REACT_APP_MANAGER_API_TOKEN || localStorage.getItem("manager_api_token") || "";
    if (k) h["X-API-KEY"] = k;
    if (t) h["X-API-TOKEN"] = t;
    return h;
  }

  useEffect(() => { setCurrentPage(1); }, [submissions, search, cardFilter]);

  function extractRows(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.submissions)) return data.submissions;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.dsars)) return data.dsars;
    return [];
  }

  const fetchSubmissions = useCallback(async () => {
    setLoading(!hasCachedDataRef.current);
    setFetchError(null);

    const isOutside = localStorage.getItem("dsar_user_type") === "outside";
    const outsideEmail = isOutside ? (localStorage.getItem("dsar_user_email") || "").toLowerCase().trim() : "";

    let tenantId = null;
    try {
      const raw = window.USER?.tenant_id || localStorage.getItem("tenant_id");
      if (raw) tenantId = Number(raw);
    } catch (e) { }

    const urls = getDsarApiUrls();
    let rows = [];
    let succeeded = false;

    console.log("[DSARDashboard] Trying URLs:", urls);

    for (const baseUrl of urls) {
      try {
        const qs = tenantId != null ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
        const res = await fetch(`${baseUrl}${qs}`, { headers: getHeaders() });

        if (res.status === 405) {
          const lr = await fetch(`${baseUrl}/list`, {
            method: "POST",
            headers: { ...getHeaders(), "Content-Type": "application/json" },
            body: JSON.stringify(tenantId != null ? { tenant_id: tenantId } : {}),
          });
          if (!lr.ok) continue;
          const data = await lr.json().catch(() => null);
          if (data == null) continue;
          rows = extractRows(data);
          succeeded = true;
          console.log("[DSARDashboard] Got", rows.length, "rows from", baseUrl, "(POST /list)");
          break;
        }

        if (res.status === 401) { setFetchError("Unauthorized — check API key / token"); break; }
        if (!res.ok) { console.warn("[DSARDashboard]", baseUrl, "→ HTTP", res.status); continue; }

        const text = await res.text();
        if (!text?.trim()) { rows = []; succeeded = true; break; }

        let data;
        try { data = JSON.parse(text); } catch (e) { continue; }
        rows = extractRows(data);
        succeeded = true;
        console.log("[DSARDashboard] Got", rows.length, "rows from", baseUrl);
        break;
      } catch (e) {
        console.warn("[DSARDashboard] Network error at", baseUrl, e.message);
        continue;
      }
    }

    if (!succeeded) {
      setFetchError(`Cannot reach DSAR API. Tried: ${urls.slice(0, 2).join(", ")} — check REACT_APP_API_BASE in .env`);
    }

    if (tenantId != null) {
      rows = rows.filter(r => {
        const rt = r.tenant_id ?? r.tenantId ?? r.tenant ?? null;
        if (rt == null || rt === "") return true;
        return Number(rt) === Number(tenantId);
      });
    }

    if (isOutside && outsideEmail) {
      rows = rows.filter(r => getRawEmail(r) === outsideEmail);
    }

    // ✅ KEY FIX: merge with existing approved/jira state so status doesn't reset
    setSubmissions(prev => {
      const prevMap = Object.fromEntries(prev.map(r => [String(r.id), r]));
      const mergedRows = rows.map(r => {
        const key = String(r.id);
        const existing = prevMap[key];
        if (!existing) return r;
        // Preserve locally-set status (approved / in-progress) if backend still shows older value
        const localStatus = existing.status;
        const backendStatus = r.status;
        const localIsNewer = ["approved", "in-progress"].includes(localStatus) && !["approved", "in-progress"].includes(backendStatus);
        return localIsNewer ? { ...r, status: localStatus } : r;
      });
      writeSessionCache(cacheKey, { submissions: mergedRows });
      hasCachedDataRef.current = true;
      return mergedRows;
    });

    setLoading(false);
  }, [cacheKey]); // eslint-disable-line

  const handleSubmitSuccess = useCallback(() => {
    setActiveSection("my-requests");
    setTimeout(() => fetchSubmissions(), 1000);
  }, [fetchSubmissions]);

  useEffect(() => {
    const isOutside = localStorage.getItem("dsar_user_type") === "outside";
    setIsOutsideUser(isOutside);
    fetchSubmissions();
  }, [fetchSubmissions]);

  useEffect(() => {
    if (getStoredTenantName()) return;
    const tenantId = window.USER?.tenant_id || localStorage.getItem("tenant_id");
    if (!tenantId) return;
    fetch(`${API_BASE}/admin/tenants`)
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        const tenants = Array.isArray(data) ? data : data?.data || [];
        const tenant = tenants.find(item => Number(item.id) === Number(tenantId));
        if (tenant?.name) setTenantName(tenant.name);
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      try {
        const payload = e?.detail || (e?.data ? (typeof e.data === 'string' ? JSON.parse(e.data) : e.data) : null) || e;
        if (!payload) return;
        fetchSubmissions();
      } catch (err) {
        // ignore
      }
    };
    document.addEventListener('consentResponse', handler);
    document.addEventListener('consent:updated', handler);
    return () => {
      document.removeEventListener('consentResponse', handler);
      document.removeEventListener('consent:updated', handler);
    };
  }, [fetchSubmissions]);

  // ─── Actions ──────────────────────────────────────────────────────────────
  const handleApprove = async (sub) => {
    if (!sub?.id || approvedIds.has(String(sub.id))) {
      await showResultAlert("Already approved", "This request was already approved earlier.", "info");
      return;
    }

    const confirm = await Swal.fire({
      title: "Approve request?",
      text: "This will approve the DSAR request and send a confirmation email.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
      cancelButtonText: "Cancel",
      customClass: { popup: "swal-popup-custom" },
    });
    if (!confirm.isConfirmed) return;

    setApproving(p => ({ ...p, [sub.id]: true }));
    showProcessingAlert("Approving request");
    try {
      try { window.showLoading?.("Approving..."); } catch (e) { }
      const base = getSafeBase();
      const kind = (sub.former_names || sub.formerNames || sub.current_address || sub.currentAddress) ? "full" : "new_request";
      const res = await fetch(`${base}/manager/dsar/${sub.id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getHeaders() },
        body: JSON.stringify({ kind }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && (json.success === undefined || json.success)) {
        setApprovedIds(prev => {
          const n = new Set(prev);
          n.add(String(sub.id));
          try { localStorage.setItem(_storageKey, JSON.stringify([...n])); } catch (e) { }
          return n;
        });
        setSubmissions(prev => prev.map(r => r.id === sub.id ? { ...r, status: "approved" } : r));
        await new Promise((resolve) => setTimeout(resolve, 900));
        await showResultAlert("Approved", "Request approved successfully.");
      } else {
        await new Promise((resolve) => setTimeout(resolve, 900));
        await showResultAlert("Approval failed", "Failed to approve: " + (json.detail || json.error || `HTTP ${res.status}`), "error");
      }
    } catch (err) {
      await new Promise((resolve) => setTimeout(resolve, 900));
      await showResultAlert("Approval failed", "Error: " + (err?.message || String(err)), "error");
    } finally {
      setApproving(p => ({ ...p, [sub.id]: false }));
      try { window.hideLoading?.(); } catch (e) { }
    }
  };

  const handleWorkflow = async (sub) => {
    const confirm = await Swal.fire({
      title: "Create Jira ticket?",
      text: "This will create a Jira ticket and mark the request as in progress.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Create",
      cancelButtonText: "Cancel",
      customClass: { popup: "swal-popup-custom" },
    });
    if (!confirm.isConfirmed) return;

    showProcessingAlert("Creating Jira ticket");
    try {
      try { window.showLoading?.("Creating Jira ticket..."); } catch (e) { }
      setSubmissions(prev => prev.map(r => r.id === sub.id ? { ...r, status: "in-progress" } : r));
      const base = getSafeBase();
      const res = await fetch(`${base}/manager/dsar/${sub.id}/jira`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getHeaders() },
        body: JSON.stringify({
          requestId: `DSAR-${String(sub.id).padStart(6, "0")}`,
          name: getName(sub),
          email: getEmail(sub),
          requestType: sub.requestType || sub.request_type || [],
          status: "in-progress",
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        const ticketKey = json.ticketKey || json.key || `DSAR-${sub.id}`;
        const ticketUrl = json.jiraUrl || json.url || json.ticketUrl || "";
        const info = { ticketKey, ticketUrl, createdAt: new Date().toISOString(), status: "created" };
        setJiraTickets(prev => {
          const u = { ...prev, [sub.id]: info };
          try { localStorage.setItem("dsar_jira_tickets", JSON.stringify(u)); } catch (e) { }
          return u;
        });
        setNotification({ type: "success", message: `✓ Jira Ticket Created: ${ticketKey}`, details: "Status: In Process", ticketUrl });
        if (ticketUrl) window.open(ticketUrl, "_blank");
        await new Promise((resolve) => setTimeout(resolve, 900));
        await showResultAlert("Jira ticket created", `Ticket ${ticketKey} was created successfully.`);
      } else {
        setNotification({ type: "warning", message: "⚠ Jira Ticket Creation Failed", details: json.detail || "", ticketUrl: "" });
        await new Promise((resolve) => setTimeout(resolve, 900));
        await showResultAlert("Jira ticket failed", json.detail || "Unable to create Jira ticket right now.", "error");
      }
    } catch (err) {
      setNotification({ type: "info", message: "ℹ Jira Error", details: err?.message || "" });
      await new Promise((resolve) => setTimeout(resolve, 900));
      await showResultAlert("Jira error", err?.message || "Unable to create Jira ticket right now.", "error");
    } finally {
      try { window.hideLoading?.(); } catch (e) { }
    }
  };

  const handleSendMail = async (sub) => {
    showProcessingAlert("Sending email");
    try {
      try { window.showLoading?.("Sending email..."); } catch (e) { }
      const base = getSafeBase();
      const res = await fetch(`${base}/manager/dsar/${sub.id}/send_mail`, {
        method: "POST", headers: { "Content-Type": "application/json", ...getHeaders() },
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success !== false) {
        await new Promise((resolve) => setTimeout(resolve, 900));
        await showResultAlert("Email sent", "Email sent successfully.");
      } else {
        await new Promise((resolve) => setTimeout(resolve, 900));
        await showResultAlert("Email failed", "Failed to send email. " + (json.detail || json.error || ""), "error");
      }
    } catch (err) {
      await new Promise((resolve) => setTimeout(resolve, 900));
      await showResultAlert("Email failed", "Failed to send email.", "error");
    } finally {
      try { window.hideLoading?.(); } catch (e) { }
    }
  };

  // const handleLogout = async () => {
  //   const confirm = await Swal.fire({
  //     title: "Logout?",
  //     text: "Are you sure you want to logout?",
  //     icon: "question",
  //     showCancelButton: true,
  //     confirmButtonText: "Logout",
  //     cancelButtonText: "Cancel",
  //     customClass: { popup: "swal-popup-custom" },
  //   });
  //   if (!confirm.isConfirmed) return;

  //   showProcessingAlert("Logging out");
  //   await new Promise((resolve) => setTimeout(resolve, 900));
  //   try { localStorage.clear(); } catch (e) {}
  //   await showResultAlert("Logged out", "You have been logged out successfully.");
  //   try { window.location.href = "/logout"; } catch (e) {}
  // };


  const handleLogout = () => {
    const confirmed = window.confirm("Are you sure you want to logout?");
    if (!confirmed) return;

    try {
      localStorage.removeItem("dsar_auth");
      localStorage.removeItem("dsar_user_type");
      localStorage.removeItem("dsar_user_email");
      localStorage.removeItem("dsar_prefill");
      localStorage.removeItem("customer_consent_email");
    } catch (e) { }

    const loginPath = isOutsideUser ? "/customer-consent-login" : "/";
    try {
      window.location.replace(loginPath);
    } catch (e) {
      try { window.location.href = loginPath; } catch (err) { }
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────
  function parseDate(raw) {
    if (!raw) return null;
    try {
      const s = String(raw).trim();
      const d = /^\d{4}-\d{2}-\d{2}[T ]/.test(s)
        ? new Date(s.replace(" ", "T") + (s.includes("Z") ? "" : "Z"))
        : new Date(s);
      if (isNaN(d)) return null;
      return {
        date: d,
        display: new Intl.DateTimeFormat("en-IN", {
          timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit",
          year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
        }).format(d),
      };
    } catch (e) { return null; }
  }

  // ─── Consent Records modal & fetcher ───────────────────────────────────
  function getConsentApiUrls() {
    const base = getSafeBase();
    const urls = [];
    if (base) {
      urls.push(`${base}/consent`);
      urls.push(`${base}/api/consent`);
    }
    urls.push('/consent');
    urls.push('/api/consent');
    if (isLocalHost() && base && !base.includes('localhost') && !base.includes('127.0.0.1')) {
      urls.push('http://localhost:8003/consent');
      urls.push('http://localhost:8003/api/consent');
    }
    return [...new Set(urls)];
  }

  const handleShowConsents = async (email, tenantId) => {
    if (!email) return;
    const emailNorm = String(email).toLowerCase().trim();

    // collect DSAR-requested types for this email (only show matching consent types)
    const requestedTypes = Array.from(new Set(
      submissions
        .filter(s => (getEmail(s) || '').toLowerCase().trim() === emailNorm)
        .flatMap(s => (getRequestTypes(s) || '').split(',').map(x => x.trim()).filter(Boolean))
    ));

    // Try to canonicalize requested types using /consent/new_types for this tenant
    let canonicalRequested = requestedTypes.slice();
    if (requestedTypes.length > 0) {
      try {
        const newTypesUrls = getConsentApiUrls().map(u => u.replace(/\/consent(\/|$)/, '/consent/new_types$1').replace('/api/consent', '/api/consent/new_types'));
        let fetched = null;
        for (const u of newTypesUrls) {
          try {
            const qs = tenantId != null ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
            const res = await fetch(u + qs, { headers: getHeaders() });
            if (!res.ok) continue;
            const txt = await res.text();
            if (!txt?.trim()) continue;
            const data = JSON.parse(txt);
            fetched = Array.isArray(data?.types) ? data.types : [];
            break;
          } catch (e) { continue; }
        }
        if (fetched && fetched.length > 0) {
          const map = {};
          for (const t of fetched) {
            try {
              const k = String(t || '').trim().toLowerCase().replace(/_/g, ' ');
              if (k) map[k] = t;
            } catch (e) { }
          }
          canonicalRequested = requestedTypes.map(rt => {
            const k = String(rt || '').trim().toLowerCase().replace(/_/g, ' ');
            return map[k] || rt;
          });
        }
      } catch (e) {
        // ignore and fall back to raw requestedTypes
      }
    }

    const urls = getConsentApiUrls();
    let rows = [];
    for (const u of urls) {
      try {
        const qs = tenantId != null ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
        const res = await fetch(u + qs, { headers: getHeaders() });
        if (!res.ok) continue;
        const txt = await res.text();
        if (!txt?.trim()) { rows = []; break; }
        const data = JSON.parse(txt);
        rows = Array.isArray(data?.consents) ? data.consents : (Array.isArray(data) ? data : []);
        break;
      } catch (e) { continue; }
    }

    // Keep only rows matching the email (loose) first
    const emailMatched = (rows || []).filter(r => {
      try {
        const re = String((r.email || r.email_address || r.emailResponse || '')).toLowerCase().trim();
        if (!re) return false;
        if (re === emailNorm) return true;
        if (re.includes(emailNorm) || emailNorm.includes(re)) return true;
        return false;
      } catch (e) { return false; }
    }).map(r => ({
      type: (r.type || r.consent_type || r.consentType || '').toString().trim(),
      created_at: r.created_at || r.given_at || r.createdAt || null,
      raw: r,
    }));

    // If requestedTypes exist, only show consent records corresponding to those requested types
    let matchedRecords = [];
    if (requestedTypes.length > 0) {
      const reqNorm = canonicalRequested.map(t => String(t).toLowerCase().trim()).filter(Boolean);
      matchedRecords = emailMatched.filter(r => {
        const t = String(r.type || '').toLowerCase().trim();
        return reqNorm.some(rt => rt === t || t.includes(rt) || rt.includes(t));
      });
      // Fallback: if strict matching yields nothing, try loose substring match against raw record JSON
      if (matchedRecords.length === 0) {
        matchedRecords = emailMatched.filter(r => {
          try {
            const raw = JSON.stringify(r.raw || {}).toLowerCase();
            return reqNorm.some(rt => (r.type || '').toLowerCase().includes(rt) || raw.includes(rt));
          } catch (e) { return false; }
        });
      }
      // Final fallback: if still empty, show all email-matched records so UI isn't empty
      if (matchedRecords.length === 0) matchedRecords = emailMatched;
    } else {
      // If no requested types, show all email-matched records
      matchedRecords = emailMatched;
    }

    // Deduplicate by normalized type: pick the most recent created_at for each type
    // const grouped = {};
    // for (const r of matchedRecords) {
    //   const key = String(r.type || '_').toLowerCase().trim() || '—';
    //   if (!grouped[key]) grouped[key] = r;
    //   else {
    //     const a = parseDate(r.created_at)?.date?.getTime() || 0;
    //     const b = parseDate(grouped[key].created_at)?.date?.getTime() || 0;
    //     if (a > b) grouped[key] = r;
    //   }
    // }
    // const unique = Object.values(grouped).map(r => ({ type: r.type || '—', created_at: r.created_at, raw: r.raw }));
    //
    // Preserve all matching consent rows and sort them newest-first so refreshes show the full history.
    const ordered = [...matchedRecords]
      .map(r => ({ type: r.type || '—', created_at: r.created_at, raw: r.raw }))
      .sort((a, b) => {
        const aTime = parseDate(a.created_at)?.date?.getTime() || 0;
        const bTime = parseDate(b.created_at)?.date?.getTime() || 0;
        return bTime - aTime;
      });

    setConsentModal({ email: emailNorm, tenantId, requestedTypes, records: ordered });
  };

  const ConsentRecordsModal = ({ modal, onClose }) => {
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    if (!modal) return null;
    const { email, records, requestedTypes } = modal;
    const rows = records || [];

    const enrich = (r) => {
      const parsed = parseDate(r.created_at);
      let st = null;
      try {
        const raw = r.raw || {};
        st = (raw.status || raw.request_status || raw.consent_status || raw.requestStatus || raw.status_type || '').toString().trim();
      } catch (e) { st = ''; }
      const statusKey = (() => {
        const s = String(st || '').toLowerCase();
        if (!s || s === 'pending' || s === 'new') return 'open';
        if (s.includes('progress')) return 'in-progress';
        if (s.includes('approve') || s === 'approved') return 'approved';
        if (s.includes('reject') || s === 'rejected' || s.includes('declin')) return 'rejected';
        return s || 'open';
      })();
      return { ...r, parsed, statusKey, statusRaw: st };
    };

    const enriched = rows.map(enrich);
    const availableTypes = Array.from(new Set(enriched.map(r => r.type).filter(Boolean)));

    const filteredByType = enriched.filter(r => typeFilter === 'all' ? true : r.type === typeFilter);

    const counts = {
      total: filteredByType.length,
      open: filteredByType.filter(r => r.statusKey === 'open').length,
      inProgress: filteredByType.filter(r => r.statusKey === 'in-progress').length,
      approved: filteredByType.filter(r => r.statusKey === 'approved').length,
      rejected: filteredByType.filter(r => r.statusKey === 'rejected').length,
    };

    const visible = filteredByType.filter(r => statusFilter === 'all' ? true : r.statusKey === statusFilter);

    return (
      <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 2200, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 780, border: '1px solid #e2e8f0', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Consent records for {email}</div>
              {requestedTypes && requestedTypes.length > 0 && <div style={{ fontSize: 12, color: '#64748b' }}>{requestedTypes.length} DSAR-request type(s) applied</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ fontSize: 12, color: '#64748b' }}>Type:</label>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                <option value="all">All ({enriched.length})</option>
                {availableTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: '#94a3b8', fontSize: 18 }}>✕</button>
            </div>
          </div>

          <div style={{ padding: 14, maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <button onClick={() => setStatusFilter(statusFilter === 'all' ? 'all' : 'all')} style={{ background: '#fff', border: statusFilter === 'all' ? '2px solid #2563eb' : '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>
                <div style={{ fontSize: 12, color: '#475569', fontWeight: 700 }}>Total: {counts.total}</div>
              </button>
              <button onClick={() => setStatusFilter(prev => prev === 'open' ? 'all' : 'open')} style={{ background: statusFilter === 'open' ? '#fefce8' : '#fff', border: statusFilter === 'open' ? '2px solid #fde047' : '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>
                <div style={{ fontSize: 12, color: '#854d0e', fontWeight: 700 }}>Open: {counts.open}</div>
              </button>
              <button onClick={() => setStatusFilter(prev => prev === 'in-progress' ? 'all' : 'in-progress')} style={{ background: statusFilter === 'in-progress' ? '#eff6ff' : '#fff', border: statusFilter === 'in-progress' ? '2px solid #93c5fd' : '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>
                <div style={{ fontSize: 12, color: '#1e40af', fontWeight: 700 }}>In Progress: {counts.inProgress}</div>
              </button>
              <button onClick={() => setStatusFilter(prev => prev === 'approved' ? 'all' : 'approved')} style={{ background: statusFilter === 'approved' ? '#f0fdf4' : '#fff', border: statusFilter === 'approved' ? '2px solid #86efac' : '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>
                <div style={{ fontSize: 12, color: '#166534', fontWeight: 700 }}>Approved: {counts.approved}</div>
              </button>
              <button onClick={() => setStatusFilter(prev => prev === 'rejected' ? 'all' : 'rejected')} style={{ background: statusFilter === 'rejected' ? '#fef2f2' : '#fff', border: statusFilter === 'rejected' ? '2px solid #fca5a5' : '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>
                <div style={{ fontSize: 12, color: '#991b1b', fontWeight: 700 }}>Rejected: {counts.rejected}</div>
              </button>
            </div>

            {visible.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>No consent records found matching the selected type.</div>
            ) : (
              <div style={{ border: '1px solid #e6eefc', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 0, background: '#f8fafc', padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                  <div>Consent Type</div>
                  <div>Created At</div>
                  <div style={{ textAlign: 'right' }}>Status</div>
                </div>
                {visible.map((r, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 0, padding: '10px 12px', background: i % 2 === 0 ? '#fff' : '#fafafa', alignItems: 'center', borderTop: '1px solid #f1f5f9' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{r.type}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{r.parsed ? r.parsed.display : '—'}</div>
                    <div style={{ textAlign: 'right' }}><StatusBadge status={r.statusKey} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  function safeVal(v) {
    if (v == null || v === "") return "—";
    if (typeof v === "string" || typeof v === "number") return String(v);
    if (Array.isArray(v)) return v.join(", ") || "—";
    return JSON.stringify(v);
  }

  // ─── Derived state ────────────────────────────────────────────────────────
  const stats = {
    total: submissions.length,
    open: submissions.filter(s => ["open", "pending", "new"].includes(getStatus(s))).length,
    inProgress: submissions.filter(s => getStatus(s) === "in-progress").length,
    approved: submissions.filter(s => getStatus(s) === "approved").length,
    rejected: submissions.filter(s => getStatus(s) === "rejected").length,
  };

  // Apply card filter first, then search
  const cardFiltered = submissions.filter(s => {
    if (!cardFilter || cardFilter === "total") return true;
    const st = getStatus(s);
    if (cardFilter === "open") return ["open", "pending", "new"].includes(st);
    if (cardFilter === "in-progress") return st === "in-progress";
    if (cardFilter === "approved") return st === "approved";
    if (cardFilter === "rejected") return st === "rejected";
    return true;
  });

  const filteredSubs = cardFiltered.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return getName(s).toLowerCase().includes(q) || getEmail(s).toLowerCase().includes(q);
  });

  const totalPages = Math.max(1, Math.ceil(filteredSubs.length / ITEMS_PER_PAGE));
  const currentItems = filteredSubs.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const sideW = collapsed ? 64 : 228;

  const handleCardClick = (key) => {
    setCardFilter(prev => prev === key ? null : key);
    setCurrentPage(1);
  };

  // ─── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: T.bg, flexDirection: "column", gap: 14 }}>
      <style>{`@keyframes dsarSpin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width: 36, height: 36, border: `3px solid ${T.border}`, borderTopColor: T.brand, borderRadius: "50%", animation: "dsarSpin 0.75s linear infinite" }} />
      <span style={{ fontSize: 14, color: T.textMuted }}>Loading dashboard…</span>
    </div>
  );

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: T.bg, fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", fontSize: 14, color: T.text }}>

      {/* Requester Detail Modal */}
      <RequesterDetailModal
        email={requesterModal?.email}
        requests={requesterModal?.requests}
        tenantName={tenantName}
        onClose={() => setRequesterModal(null)}
        slaDays={slaDays}
        jiraTickets={jiraTickets}
        approvedIds={approvedIds}
      />

      {/* Submission Detail Modal */}
      <SubmissionDetailModal
        sub={detailSub}
        tenantName={tenantName}
        onClose={() => setDetailSub(null)}
        slaDays={slaDays}
        jiraTickets={jiraTickets}
        approvedIds={approvedIds}
      />
      {/* Consent Records Modal */}
      <ConsentRecordsModal modal={consentModal} onClose={() => setConsentModal(null)} />
      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: 18, right: 18, zIndex: 9999, background: toast.type === "error" ? "#991b1b" : "#166534", color: "#fff", padding: "11px 16px", borderRadius: 10, display: "flex", alignItems: "center", gap: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", fontSize: 13, fontWeight: 500, minWidth: 240, maxWidth: 340 }}>
          <span>{toast.type === "error" ? "✕" : "✓"}</span>
          <span style={{ flex: 1 }}>{toast.msg}</span>
          <button onClick={() => setToast(null)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
        </div>
      )}

      {/* Jira notification banner */}
      {notification && (
        <div style={{ position: "fixed", top: 0, left: sideW, right: 0, zIndex: 1000, background: notification.type === "success" ? "#166634" : notification.type === "warning" ? "#92400e" : "#1e40af", color: "#fff", padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", transition: "left 0.2s" }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{notification.message}</div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>{notification.details}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {notification.ticketUrl && (
              <button onClick={() => window.open(notification.ticketUrl, "_blank")} style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", padding: "4px 10px", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>Open Jira</button>
            )}
            <button onClick={() => setNotification(null)} style={{ background: "transparent", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
          </div>
        </div>
      )}

      {/* ══ SIDEBAR ══ */}
      <aside style={{ width: sideW, height: "100vh", background: "#fff", borderRight: "1px solid #e5e7eb", display: "flex", flexDirection: "column", transition: "width 0.2s ease", flexShrink: 0, overflowX: "hidden", overflowY: "auto" }}>
        <div style={{ padding: "12px 10px", borderBottom: "1px solid #e5e7eb" }}>
          <button onClick={() => setCollapsed(!collapsed)} style={{ width: "100%", padding: "8px 0", border: "1px solid #e5e7eb", borderRadius: 8, background: "#f9fafb", color: "#4b5563", cursor: "pointer", fontSize: 12.5, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, transition: "all 0.15s ease" }}>
            {collapsed ? <ChevronRight size={16} color="#4b5563" /> : <><ChevronLeft size={16} color="#4b5563" /><span>Collapse</span></>}
          </button>
        </div>
        <nav style={{ flex: 1, padding: "12px 10px" }}>
          {[
            { key: "my-requests", icon: ClipboardList, color: "#2563eb", label: "My Requests" },
            { key: "new-request", icon: PlusCircle, color: "#16a34a", label: "New Request" },
            { key: "your-rights", icon: Scale, color: "#ea580c", label: "Your Rights" },
            { key: "requester-summary", icon: Users, color: "#7c3aed", label: "Requester Summary" },
            { key: "help", icon: HelpCircle, color: "#dc2626", label: "Help & Support" },
          ].map(({ key, icon: IconComp, color, label }) => {
            const isActive = activeSection === key;
            return (
              <NavItem
                key={key}
                icon={<IconComp size={18} style={{ color: isActive ? "#2563eb" : color }} />}
                label={label}
                active={isActive}
                collapsed={collapsed}
                onClick={() => setActiveSection(key)}
              />
            );
          })}
        </nav>
        {isOutsideUser && (
          <button type="button" onClick={handleLogout} title={collapsed ? "Logout" : ""} style={{ width: "100%", padding: "8px 0", border: "1px solid #fca5a5", borderRadius: 8, background: "#fef2f2", color: "#991b1b", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 6, paddingLeft: collapsed ? 0 : 10 }}>
            <span style={{ fontSize: 15 }}>🔓</span>
            {!collapsed && <span>Logout</span>}
          </button>
        )}
      </aside>

      {/* ══ MAIN ══ */}
      <main style={{ flex: 1, height: "100vh", overflowY: "auto", overflowX: "auto", padding: "28px 32px", minWidth: 0, marginTop: notification ? 44 : 0 }}>

        {/* ─── MY REQUESTS ─── */}
        {activeSection === "my-requests" && (
          <>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, gap: 12, flexWrap: "nowrap" }}>
              <div style={{ minWidth: 0 }}>
                <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: T.text }}>DSAR Overview</h1>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: T.textMuted }}>Data Subject Access Request management</p>
              </div>
              <button
                onClick={() => {
                  if (isOutsideUser) {
                    window.location.replace("/customer-consent-login");
                  } else {
                    window.history.back();
                  }
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  background: "#fff",
                  color: T.text,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  flexShrink: 0
                }}
              >
                <span aria-hidden="true">←</span>
                <span>Back</span>
              </button>
            </div>

            {/* Error banner */}
            {fetchError && (
              <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", marginBottom: 16, color: "#991b1b", fontSize: 13, display: "flex", alignItems: "center", gap: 10 }}>
                <span>⚠ {fetchError}</span>
                <button onClick={fetchSubmissions} style={{ background: "none", border: "none", color: T.brand, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>↻ Retry</button>
              </div>
            )}

            {/* ── CLICKABLE STAT CARDS ── */}
            <div style={{ marginBottom: 8 }}>
              {cardFilter && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, padding: "8px 14px", background: T.brandLight, border: `1px solid ${T.brandBorder}`, borderRadius: 8, fontSize: 13 }}>
                  <span style={{ color: T.brand, fontWeight: 600 }}>
                    🔍 Showing: <strong style={{ textTransform: "capitalize" }}>{cardFilter === "in-progress" ? "In Progress" : cardFilter}</strong> — <span title={Number(filteredSubs.length).toLocaleString()}>{formatCompactNumber(filteredSubs.length)} record{filteredSubs.length !== 1 ? "s" : ""}</span>
                  </span>
                  <button onClick={() => setCardFilter(null)} style={{ marginLeft: "auto", background: "none", border: "none", color: T.brand, cursor: "pointer", fontWeight: 700, fontSize: 12 }}>Clear ✕</button>
                </div>
              )}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <StatCard label="Total" value={stats.total} accent="#94a3b8" active={cardFilter === "total"} onClick={() => handleCardClick("total")} />
                <StatCard label="Open" value={stats.open} accent="#f59e0b" active={cardFilter === "open"} onClick={() => handleCardClick("open")} />
                <StatCard label="In Progress" value={stats.inProgress} accent="#3b82f6" active={cardFilter === "in-progress"} onClick={() => handleCardClick("in-progress")} />
                <StatCard label="Approved" value={stats.approved} accent="#22c55e" active={cardFilter === "approved"} onClick={() => handleCardClick("approved")} />
                <StatCard label="Rejected" value={stats.rejected} accent="#ef4444" active={cardFilter === "rejected"} onClick={() => handleCardClick("rejected")} />
              </div>
            </div>

            {/* Toolbar */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0 14px", gap: 12, flexWrap: "wrap" }}>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.textMuted, fontSize: 14, pointerEvents: "none" }}>🔍</span>
                <input type="text" placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)}
                  style={{ paddingLeft: 32, paddingRight: 12, height: 34, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, background: T.surface, color: T.text, outline: "none", width: 240 }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{ fontSize: 13, color: T.textSec, fontWeight: 500 }}>SLA:</label>
                <select value={slaDays} onChange={e => { const v = Number(e.target.value); setSlaDays(v); localStorage.setItem("dsar_sla_days", v); }}
                  style={{ height: 34, padding: "0 10px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, background: T.surface, color: T.text }}>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                </select>
                <span style={{ fontSize: 12, color: T.textMuted }}>Manager SLA</span>
              </div>
            </div>

            {/* Table */}
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "linear-gradient(135deg,#60a5fa,#2563eb)" }}>
                      {["S.No", "Request ID", "Name", "Email", "Phone", "Request Type", "Status", "Submitted On", "SLA", "Jira", "Count"].map(h => (
                        <th key={h} style={{ padding: "13px 12px", textAlign: "center", fontSize: 11.5, fontWeight: 700, color: "#fff", letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "nowrap", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length === 0 ? (
                      <tr><td colSpan={11} style={{ textAlign: "center", padding: "52px 0", color: T.textMuted, fontSize: 14 }}>
                        {cardFilter || search ? "No records match the current filter." : "No DSAR submissions found."}
                      </td></tr>
                    ) : currentItems.map((sub, idx) => {
                      const name = getName(sub) || "—";
                      const email = getEmail(sub) || "—";
                      const phone = getPhone(sub) || "—";
                      const reqType = getRequestTypes(sub) || "—";
                      const status = getStatus(sub);
                      const parsed = parseDate(getDate(sub));
                      const diffDays = parsed ? (Date.now() - parsed.date) / 864e5 : null;
                      const isBreached = diffDays != null ? diffDays > slaDays : false;
                      const jiraInfo = jiraTickets[sub.id];
                      const jiraStatus = jiraInfo?.status === "created" ? "Created" : jiraInfo?.status === "creating" ? "In Process" : "Not Created";
                      const isApproved = approvedIds.has(String(sub.id)) || status === "approved";
                      const isBusy = !!approving[sub.id];
                      const emailCount = email !== "—" ? submissions.filter(s => getEmail(s) === email).length : 0;
                      const rowBg = idx % 2 === 0 ? T.surface : T.surfaceAlt;

                      return (
                        <tr key={sub._id || sub.id}
                          style={{ borderBottom: `1px solid ${T.border}`, background: rowBg, transition: "background 0.1s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#eef2ff"}
                          onMouseLeave={e => e.currentTarget.style.background = rowBg}
                        >
                          <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 600, color: T.textSec, whiteSpace: "nowrap" }}>
                            {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                          </td>
                          {/* <td style={{ padding: "10px 12px" }}>
                            <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                              <IconBtn title={jiraInfo ? `Jira: ${jiraInfo.ticketKey}` : "Create Jira Ticket"} onClick={() => handleWorkflow(sub)} tone={jiraInfo ? "green" : "blue"}>
                                {jiraInfo ? "✓" : "⚙"}
                              </IconBtn>
                              <IconBtn title={isApproved ? "Already approved" : "Approve & email user"} disabled={isApproved || isBusy} onClick={() => handleApprove(sub)} tone="green">
                                {isBusy ? "…" : "✓"}
                              </IconBtn>
                              {isBreached && <IconBtn title="Send Breach Email" onClick={() => handleSendMail(sub)} tone="amber">✉</IconBtn>}
                            </div>
                          </td> */}
                          <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                            <span style={{ fontSize: 11.5, fontFamily: "monospace", color: T.brand, fontWeight: 700, background: T.brandLight, padding: "2px 7px", borderRadius: 5, border: `1px solid ${T.brandBorder}` }}>
                              DSAR-{String(sub.id).padStart(6, "0")}
                            </span>
                          </td>
                          {/* <td style={{ padding:"10px 12px", fontWeight:500, color:T.text, whiteSpace:"nowrap" }}>{safeVal(name)}</td> */}
                          <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                            <span
                              onClick={() => setDetailSub(sub)}
                              style={{ fontWeight: 600, color: T.brand, cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}
                              title="Click to view full details"
                            >{safeVal(name)}</span>
                          </td>
                          {/* <td style={{ padding:"10px 12px", color:T.textSec, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{safeVal(email)}</td> */}
                          <td style={{ padding: "10px 12px", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            <span
                              onClick={() => setDetailSub(sub)}
                              style={{ color: T.brand, cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3, fontSize: 13 }}
                              title="Click to view full details"
                            >{safeVal(email)}</span>
                          </td>
                          <td style={{ padding: "10px 12px", color: T.textSec, whiteSpace: "nowrap" }}>{safeVal(phone)}</td>
                          <td style={{ padding: "10px 12px", color: T.textSec, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={reqType}>{safeVal(reqType)}</td>
                          <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}><StatusBadge status={status} /></td>
                          <td style={{ padding: "10px 12px", color: T.textSec, fontSize: 12, whiteSpace: "nowrap" }}>
                            {parsed ? parsed.display : <span style={{ color: T.textMuted }}>—</span>}
                          </td>
                          <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <SlaBadge isBreached={isBreached} noDate={!parsed} />
                              {/* <IconBtn title="Send Breach Email" onClick={() => handleSendMail(sub)} tone="amber">✉</IconBtn> */}
                            </div>
                          </td>
                          <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <JiraBadge status={jiraStatus} />
                              {jiraStatus !== "Created" && <IconBtn title="Create Jira Ticket" disabled={jiraStatus === "In Process"} onClick={() => handleWorkflow(sub)} tone="blue">🎟</IconBtn>}
                              {jiraStatus === "Created" && jiraInfo?.ticketUrl && (
                                <a href={jiraInfo.ticketUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: T.brand, textDecoration: "none", fontWeight: 600 }}>↗</a>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: "10px 12px", textAlign: "center" }}>
                            <div onClick={() => handleShowConsents(email, sub.tenant_id ?? sub.tenantId ?? null)} title="View consent records" style={{ cursor: "pointer", width: 28, height: 28, borderRadius: 6, margin: "0 auto", background: emailCount > 1 ? "#fefce8" : T.surfaceAlt, border: `1px solid ${emailCount > 1 ? "#fde047" : T.border}`, color: emailCount > 1 ? "#854d0e" : T.textSec, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12 }}>
                              {emailCount}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderTop: `1px solid ${T.border}`, background: "#f8fafc", flexWrap: "wrap", gap: 12 }}>
                  <span style={{ fontSize: 13, color: T.textMuted }}>
                    Showing <b style={{ color: T.text }}>{(currentPage - 1) * ITEMS_PER_PAGE + 1}</b>–<b style={{ color: T.text }}>{Math.min(currentPage * ITEMS_PER_PAGE, filteredSubs.length)}</b> of <b style={{ color: T.brand }} title={Number(filteredSubs.length).toLocaleString()}>{formatCompactNumber(filteredSubs.length)}</b>
                  </span>
                  <div style={{ display: "flex", gap: 5 }}>
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} style={{ padding: "7px 13px", borderRadius: 8, border: `1px solid ${T.border}`, background: currentPage === 1 ? "#f1f5f9" : "#fff", color: currentPage === 1 ? "#94a3b8" : T.text, cursor: currentPage === 1 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600 }}>← Prev</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(currentPage - p) <= 1)
                      .map((p, i, a) => (
                        <React.Fragment key={p}>
                          {a[i - 1] && p - a[i - 1] > 1 && <span style={{ padding: "0 3px", color: T.textMuted, lineHeight: "36px" }}>…</span>}
                          <button onClick={() => setCurrentPage(p)} style={{ minWidth: 36, height: 36, borderRadius: 8, border: p === currentPage ? `1px solid ${T.brand}` : `1px solid ${T.border}`, background: p === currentPage ? "#2563eb" : "#fff", color: p === currentPage ? "#fff" : T.textSec, fontWeight: p === currentPage ? 700 : 500, fontSize: 13, cursor: "pointer" }}>{p}</button>
                        </React.Fragment>
                      ))}
                    <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} style={{ padding: "7px 13px", borderRadius: 8, border: `1px solid ${T.border}`, background: currentPage === totalPages ? "#f1f5f9" : "#fff", color: currentPage === totalPages ? "#94a3b8" : T.text, cursor: currentPage === totalPages ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600 }}>Next →</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {activeSection === "new-request" && <div className="content-section"><NewRequest onSubmitSuccess={handleSubmitSuccess} /></div>}
        {activeSection === "your-rights" && <div className="content-section"><YourRights onMenu={setActiveSection} /></div>}
        {activeSection === "help" && (
          <div className="content-section">
            <Help tenantId={window.USER?.tenant_id || localStorage.getItem("tenant_id")} />
          </div>
        )}

        {/* ─── REQUESTER SUMMARY ─── */}
        {activeSection === "requester-summary" && (
          <div className="content-section">
            <div style={{ marginBottom: 24 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: T.text }}>Requester Summary</h1>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: T.textMuted }}>
                <span title={Number(Object.keys(submissions.reduce((a, s) => { a[getEmail(s) || "unknown"] = 1; return a; }, {})).length).toLocaleString()}>{formatCompactNumber(Object.keys(submissions.reduce((a, s) => { a[getEmail(s) || "unknown"] = 1; return a; }, {})).length)}</span> unique requesters
              </p>
            </div>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
                    {["#", "Requester Email", "Total", "Open", "In Progress", "Approved", "Rejected"].map(h => (
                      <th key={h} style={{ padding: "11px 14px", textAlign: ["#", "Total", "Open", "In Progress", "Approved", "Rejected"].includes(h) ? "center" : "left", fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const map = submissions.reduce((acc, sub) => {
                      const k = getEmail(sub) || "unknown";
                      if (!acc[k]) acc[k] = { email: k, count: 0, open: 0, inProgress: 0, approved: 0, rejected: 0 };
                      acc[k].count++;
                      const st = getStatus(sub);
                      if (["open", "pending", "new"].includes(st)) acc[k].open++;
                      else if (st === "in-progress") acc[k].inProgress++;
                      else if (st === "approved") acc[k].approved++;
                      else if (st === "rejected") acc[k].rejected++;
                      return acc;
                    }, {});
                    const b = (n, bg, color, border) => n > 0
                      ? <Pill bg={bg} color={color} border={border}><span title={Number(n).toLocaleString()}>{formatCompactNumber(n)}</span></Pill>
                      : <span style={{ color: T.textMuted }}>—</span>;
                    return Object.values(map).map((item, idx) => {
                      const rowBg = idx % 2 === 0 ? T.surface : T.surfaceAlt;
                      return (
                        <tr key={item.email} style={{ borderBottom: `1px solid ${T.border}`, background: rowBg }}
                          onMouseEnter={e => e.currentTarget.style.background = "#eef2ff"}
                          onMouseLeave={e => e.currentTarget.style.background = rowBg}>
                          <td style={{ padding: "12px 14px", textAlign: "center", color: T.textMuted, fontSize: 12 }}>{idx + 1}</td>
                          {/* <td style={{ padding:"12px 14px" }}>
                            <div style={{ fontWeight:600, color:T.text }}>{item.email}</div>
                            <div style={{ fontSize:11, color:T.textMuted, marginTop:2 }}>{item.count} request{item.count!==1?"s":""}</div>
                          </td> */}
                          <td style={{ padding: "12px 14px" }}>
                            <div
                              onClick={() => setRequesterModal({
                                email: item.email,
                                requests: submissions.filter(s => getEmail(s) === item.email),
                              })}
                              style={{ fontWeight: 600, color: T.brand, cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}
                              title="Click to view all requests"
                            >
                              {item.email}
                            </div>
                            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{item.count} request{item.count !== 1 ? "s" : ""}</div>
                          </td>
                          <td style={{ textAlign: "center", padding: "12px 14px", fontWeight: 700 }}>{item.count}</td>
                          <td style={{ textAlign: "center", padding: "12px 14px" }}>{b(item.open, "#fefce8", "#854d0e", "#fde047")}</td>
                          <td style={{ textAlign: "center", padding: "12px 14px" }}>{b(item.inProgress, "#eff6ff", "#1e40af", "#93c5fd")}</td>
                          <td style={{ textAlign: "center", padding: "12px 14px" }}>{b(item.approved, "#f0fdf4", "#166534", "#86efac")}</td>
                          <td style={{ textAlign: "center", padding: "12px 14px" }}>{b(item.rejected, "#fef2f2", "#991b1b", "#fca5a5")}</td>
                        </tr>
                      );
                    });
                  })()}
                  {submissions.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: "center", padding: "48px 0", color: T.textMuted }}>No submissions found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DSARDashboardUser;